import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2, Circle, Radio, Music, Globe, Zap, Users, Headphones,
  ChevronRight, X, Sparkles, ArrowRight
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

/* ─── Welcome Modal (shown once on first login) ─── */
export const WelcomeModal = ({ currentUser, onClose }) => {
  const isListener = currentUser?.plan === "listener" || currentUser?.user_type === "listener";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="welcome-modal">
      <div className="bg-[#111318] border border-white/10 rounded-2xl max-w-md w-full overflow-hidden">
        {/* Header gradient */}
        <div className="relative h-32 bg-gradient-to-br from-purple-600/30 via-purple-500/10 to-transparent flex items-center justify-center">
          <div className="w-16 h-16 bg-purple-500/20 border border-purple-500/30 rounded-2xl flex items-center justify-center">
            {isListener ? <Headphones className="w-8 h-8 text-purple-400" /> : <Radio className="w-8 h-8 text-purple-400" />}
          </div>
          <button onClick={onClose} className="absolute top-3 right-3 text-white/30 hover:text-white/70 transition" data-testid="welcome-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 text-center">
          <h2 className="text-xl font-bold text-white mb-1" data-testid="welcome-heading">
            Welcome to DJsandMCMedia{currentUser?.name ? `, ${currentUser.name.split(" ")[0]}` : ""}!
          </h2>
          <p className="text-white/50 text-sm mb-6">
            {isListener
              ? "You're all set to discover amazing radio stations, follow your favorites, and never miss a live set."
              : "Your radio station is almost ready. Complete a few quick steps and you'll be broadcasting in no time."}
          </p>

          <div className="space-y-3 text-left mb-6">
            {(isListener
              ? [
                  { icon: Users, text: "Follow stations you love" },
                  { icon: Headphones, text: "Get notified when they go live" },
                  { icon: Music, text: "Discover new artists & tracks" },
                ]
              : [
                  { icon: Radio, text: "Connect your stream & go live" },
                  { icon: Globe, text: "Get your own branded player page" },
                  { icon: Zap, text: "Track airplay analytics in real-time" },
                ]
            ).map((item, i) => (
              <div key={i} className="flex items-center gap-3 bg-white/[0.03] border border-white/5 rounded-xl px-4 py-3">
                <div className="w-8 h-8 bg-purple-500/15 rounded-lg flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 text-purple-400" />
                </div>
                <span className="text-white/80 text-sm">{item.text}</span>
              </div>
            ))}
          </div>

          <button
            onClick={onClose}
            className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2"
            data-testid="welcome-get-started"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Checklist Item ─── */
const ChecklistItem = ({ done, label, action, actionLabel, testId }) => (
  <div className={`flex items-center justify-between py-3 ${done ? "opacity-50" : ""}`} data-testid={testId}>
    <div className="flex items-center gap-3">
      {done ? (
        <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
      ) : (
        <Circle className="w-5 h-5 text-white/20 flex-shrink-0" />
      )}
      <span className={`text-sm ${done ? "text-white/40 line-through" : "text-white/80"}`}>{label}</span>
    </div>
    {!done && action && (
      <button
        onClick={action}
        className="text-purple-400 hover:text-purple-300 text-xs font-medium flex items-center gap-1 transition"
      >
        {actionLabel || "Do it"} <ChevronRight className="w-3 h-3" />
      </button>
    )}
  </div>
);

/* ─── Onboarding Checklist Card ─── */
export const OnboardingChecklist = ({ currentUser, authToken, onTabChange }) => {
  const navigate = useNavigate();
  const isListener = currentUser?.plan === "listener" || currentUser?.user_type === "listener";
  const [dismissed, setDismissed] = useState(false);
  const [followCount, setFollowCount] = useState(null);

  useEffect(() => {
    const key = `onboarding_checklist_dismissed_${currentUser?.user_id}`;
    if (localStorage.getItem(key) === "true") setDismissed(true);
  }, [currentUser?.user_id]);

  // Check if user follows any station
  useEffect(() => {
    if (!isListener || !authToken) return;
    fetch(`${BACKEND_URL}/api/follows/my-follows`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((d) => setFollowCount(Array.isArray(d) ? d.length : 0))
      .catch(() => setFollowCount(0));
  }, [isListener, authToken]);

  if (dismissed) return null;

  const handleDismiss = () => {
    const key = `onboarding_checklist_dismissed_${currentUser?.user_id}`;
    localStorage.setItem(key, "true");
    setDismissed(true);
  };

  /* ─── Build checklist per user type ─── */
  const stationOwnerItems = [
    {
      id: "verify-email",
      label: "Verify your email",
      done: !!currentUser?.email_verified,
      actionLabel: "Check inbox",
    },
    {
      id: "station-name",
      label: "Set your station name",
      done: !!(currentUser?.station_name && currentUser.station_name !== `${currentUser.name}'s Station`),
      action: () => onTabChange?.("settings"),
      actionLabel: "Settings",
    },
    {
      id: "connect-stream",
      label: "Connect your stream",
      done: !!(currentUser?.azuracast_station_id || currentUser?.external_stream?.stream_url),
      action: () => onTabChange?.("station"),
      actionLabel: "My Channel",
    },
    {
      id: "customize-player",
      label: "Customize your player",
      done: false, // We can't easily check this from user obj
      action: () => onTabChange?.("whitelabel"),
      actionLabel: "White Label",
    },
    {
      id: "submit-track",
      label: "Submit your first track",
      done: false,
      action: () => navigate("/submit"),
      actionLabel: "Submit",
    },
    {
      id: "setup-embed",
      label: "Set up your embed player",
      done: false,
      action: () => onTabChange?.("embed"),
      actionLabel: "Embed Hub",
    },
  ];

  const listenerItems = [
    {
      id: "verify-email",
      label: "Verify your email",
      done: !!currentUser?.email_verified,
      actionLabel: "Check inbox",
    },
    {
      id: "follow-station",
      label: "Follow a station",
      done: followCount > 0,
      action: () => navigate("/discover"),
      actionLabel: "Discover",
    },
    {
      id: "explore-discover",
      label: "Explore the Discover page",
      done: false,
      action: () => navigate("/discover"),
      actionLabel: "Explore",
    },
    {
      id: "listen-live",
      label: "Listen to a live station",
      done: false,
      action: () => navigate("/discover"),
      actionLabel: "Listen",
    },
  ];

  const items = isListener ? listenerItems : stationOwnerItems;
  const completedCount = items.filter((i) => i.done).length;
  const progress = Math.round((completedCount / items.length) * 100);

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 mb-6" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-purple-500/15 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">
              {isListener ? "Getting Started" : "Launch Checklist"}
            </h3>
            <p className="text-white/40 text-xs">
              {completedCount}/{items.length} complete
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-white/20 hover:text-white/50 transition"
          data-testid="onboarding-dismiss"
          title="Dismiss checklist"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-white/5 rounded-full mb-4 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
          style={{ width: `${progress}%` }}
          data-testid="onboarding-progress"
        />
      </div>

      {/* Checklist items */}
      <div className="divide-y divide-white/5">
        {items.map((item) => (
          <ChecklistItem
            key={item.id}
            done={item.done}
            label={item.label}
            action={item.action}
            actionLabel={item.actionLabel}
            testId={`checklist-${item.id}`}
          />
        ))}
      </div>

      {completedCount === items.length && (
        <div className="mt-4 bg-green-500/10 border border-green-500/20 rounded-xl p-3 text-center">
          <p className="text-green-400 text-sm font-medium">All done! You're all set.</p>
          <button onClick={handleDismiss} className="text-green-400/60 text-xs mt-1 hover:text-green-300 transition">
            Dismiss checklist
          </button>
        </div>
      )}
    </div>
  );
};
